import { useId } from 'react';
import type { ChangeEvent, InputHTMLAttributes, JSX } from 'react';
import { useFieldAria } from './Field';
import { CONTROL_HEIGHT, FOCUS_RING, INPUT_BASE, cx } from './styles';

export interface TextFieldProps
  extends Omit<
    InputHTMLAttributes<HTMLInputElement>,
    | 'aria-describedby'
    | 'children'
    | 'className'
    | 'disabled'
    | 'id'
    | 'onChange'
    | 'required'
    | 'size'
    | 'value'
  > {
  value: string;
  onChange: (value: string) => void;
  className?: string;
  id?: string;
  /** 由调用方判定的非法状态（重名、格式不符…）；在 `Field` 里则由 `error` 自动带上 */
  invalid?: boolean;
  required?: boolean;
  disabled?: boolean;
  size?: 'sm' | 'md' | 'lg';
  'aria-describedby'?: string;
}

export function TextField({
  value,
  onChange,
  className,
  id,
  invalid = false,
  required = false,
  disabled = false,
  size = 'md',
  'aria-describedby': ariaDescribedBy,
  ...rest
}: TextFieldProps): JSX.Element {
  const field = useFieldAria();
  const generatedId = useId().replaceAll(':', '');

  // 在 Field 里时以 Field 的 id 为准：label 的 htmlFor 已经指向它，控件另起一个 id 会断开关联
  const controlId = field === null ? (id ?? `text-${generatedId}`) : field.controlId;
  const isRequired = required || field?.required === true;
  const isDisabled = disabled || field?.disabled === true;
  const isInvalid = invalid || field?.invalid === true;
  const describedBy = cx(field?.describedBy, ariaDescribedBy);

  function handleChange(event: ChangeEvent<HTMLInputElement>): void {
    onChange(event.target.value);
  }

  return (
    <input
      {...rest}
      id={controlId}
      type={rest.type ?? 'text'}
      value={value}
      onChange={handleChange}
      required={isRequired ? true : undefined}
      disabled={isDisabled}
      aria-invalid={isInvalid ? true : undefined}
      aria-describedby={describedBy === '' ? undefined : describedBy}
      className={cx(INPUT_BASE, CONTROL_HEIGHT[size], 'px-2 py-1', FOCUS_RING, className)}
    />
  );
}
